import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Button } from '@claritio/frontend/components/ui/button'
import OrbPresence from '@claritio/frontend/components/interview/OrbPresence'
import { PageLoading } from '@claritio/frontend/components/PageBoundary'
import { cn } from '@claritio/frontend/lib/utils'
import { lazyPage } from '@claritio/frontend/lib/lazyPage'

const ChatPage = lazyPage(() => import('@claritio/frontend/pages/Chat/ChatPage'))

export const CLARA_PANEL_ID = 'clara-panel'
const ENTER = 'cubic-bezier(0, 0, 0.38, 0.9)'
const EXIT = 'cubic-bezier(0.2, 0, 1, 0.9)'
// The circle's centre: 20px from the corner plus half of its 44px.
const ORIGIN = 'calc(100% - 42px) calc(100% - 42px)'
const PANEL_WIDTH = 380

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
export const isClaraShortcut = (e: KeyboardEvent) => e.key.toLowerCase() === 'j' && (isMac() ? e.metaKey : e.ctrlKey) && !e.altKey && !e.shiftKey

// The panel grows out of the circle and shrinks back into it (design rules §10).
function reveal(el: HTMLElement, opening: boolean) {
  if (reducedMotion() || typeof el.animate !== 'function') return null
  const desktop = matchMedia('(min-width: 768px)').matches
  const closed = { clipPath: `circle(0px at ${ORIGIN})`, ...(desktop && { width: '0px' }) }
  const shown = { clipPath: `circle(150% at ${ORIGIN})`, ...(desktop && { width: `${PANEL_WIDTH}px` }) }
  return el.animate(opening ? [closed, shown] : [shown, closed], { duration: opening ? 280 : 200, easing: opening ? ENTER : EXIT })
}

// Clara beside any screen: the same chat as Ask Clara, in a side panel that
// pushes the page aside instead of covering its actions.
export function ClaraPanel({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const { t } = useTranslation('admin')
  const ref = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const closing = useRef(false)
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose })

  const requestClose = () => {
    if (closing.current) return
    closing.current = true
    const animation = ref.current && reveal(ref.current, false)
    if (!animation) return onCloseRef.current()
    animation.onfinish = () => onCloseRef.current()
  }
  const requestCloseRef = useRef(requestClose)
  useEffect(() => { requestCloseRef.current = requestClose })

  useLayoutEffect(() => { if (ref.current) reveal(ref.current, true) }, [])

  useEffect(() => {
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'Escape' && !e.defaultPrevented) || isClaraShortcut(e)) {
        e.preventDefault()
        requestCloseRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <aside id={CLARA_PANEL_ID} ref={ref} aria-label={t('clara.panel.title')}
      className="flex min-h-0 flex-col overflow-hidden bg-content max-md:fixed max-md:inset-0 max-md:z-50 md:w-[380px] md:shrink-0 md:border-l print:hidden">
      <div className="flex min-w-[380px] items-center justify-between gap-2 py-3 pl-5 pr-3 max-md:min-w-0">
        <span className="flex items-center gap-2.5">
          {/* At this size the state ring would crowd the mark, so the header shows the mark alone. */}
          <OrbPresence agentState={null} className="size-7 before:hidden md:size-7" />
          <h2 className="text-title">{t('clara.panel.title')}</h2>
        </span>
        <span className="flex items-center gap-1">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/chat?org_id=${orgId}`} onClick={onClose}>{t('clara.panel.openFull')}</Link>
          </Button>
          <Button ref={closeRef} type="button" variant="ghost" size="icon" className="size-8" onClick={requestClose}
            aria-label={t('clara.panel.close')} title={t('clara.panel.close')}>
            <X className="size-4" />
          </Button>
        </span>
      </div>
      <div className="flex min-h-0 min-w-[380px] flex-1 flex-col max-md:min-w-0">
        <Suspense fallback={<PageLoading />}>
          <ChatPage variant="panel" orgId={orgId} />
        </Suspense>
      </div>
    </aside>
  )
}

// Two slow breaths the first time the circle appears in a visit, then it stays still.
let invited = false

export function ClaraTrigger({ onOpen, clientName, takeFocus }: { onOpen: () => void; clientName?: string | null; takeFocus?: () => boolean }) {
  const { t } = useTranslation('admin')
  const ref = useRef<HTMLButtonElement>(null)
  const glow = useRef<HTMLSpanElement>(null)
  const [shortcut] = useState(() => (isMac() ? '⌘J' : 'Ctrl J'))

  const takeFocusRef = useRef(takeFocus)
  useEffect(() => {
    if (takeFocusRef.current?.()) ref.current?.focus()
    if (invited || reducedMotion() || typeof glow.current?.animate !== 'function') return
    invited = true
    glow.current.animate(
      [{ opacity: 0.25, transform: 'scale(1)' }, { opacity: 1, transform: 'scale(1.35)' }, { opacity: 0.25, transform: 'scale(1)' }],
      { duration: 2400, iterations: 2, easing: 'ease-in-out' },
    )
  }, [])

  return (
    <button ref={ref} type="button" onClick={onOpen} onPointerEnter={ChatPage.preload} onFocus={ChatPage.preload} aria-label={t('clara.panel.open')} aria-expanded={false} aria-controls={CLARA_PANEL_ID}
      aria-keyshortcuts="Meta+J Control+J"
      className="group absolute bottom-5 right-5 z-30 flex items-center gap-2.5 rounded-full focus-visible:outline-none print:hidden">
      <span aria-hidden="true"
        className={cn('pointer-events-none flex translate-x-1.5 items-center gap-3 rounded-2xl bg-card px-3.5 py-2 text-right opacity-0 shadow-[var(--shadow-float)] ring-1 ring-border',
          'transition-[opacity,transform] duration-[var(--duration-fast)] ease-[var(--ease-enter)] group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 max-md:hidden')}>
        <span className="grid">
          <span className="whitespace-nowrap text-body font-medium">{t('clara.panel.open')}</span>
          {clientName && <span className="whitespace-nowrap text-caption text-muted-foreground">{t('clara.panel.about', { name: clientName })}</span>}
        </span>
        <kbd className="rounded-md bg-muted px-1.5 text-caption text-muted-foreground">{shortcut}</kbd>
      </span>
      <span className="relative isolate grid size-11 place-items-center rounded-full shadow-[0_0_0_4px_var(--mist),0_10px_24px_-8px_color-mix(in_srgb,var(--brand)_45%,transparent)] group-focus-visible:outline-2 group-focus-visible:outline-offset-4 group-focus-visible:outline-ring">
        <span ref={glow} aria-hidden="true" className="absolute -inset-1.5 -z-10 rounded-full bg-[radial-gradient(circle,color-mix(in_srgb,var(--brand)_35%,transparent),transparent_70%)] opacity-0" />
        <OrbPresence agentState={null} className="size-11 before:hidden md:size-11" />
      </span>
    </button>
  )
}
